(() => {
  const money = (cents) => new Intl.NumberFormat(window.Shopify?.locale || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const icon = (name, size = 14) => {
    const paths = {
      minus: '<path d="M5 12h14"/>',
      plus: '<path d="M5 12h14"/><path d="M12 5v14"/>',
      trash: '<path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>'
    };
    return `<svg class="icon" xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${paths[name] || ''}</svg>`;
  };
  document.documentElement.classList.remove('no-js');

  function initAnnouncementTimers(root = document) {
    qsa('[data-countdown]', root).forEach((el) => {
      const duration = Number(el.dataset.duration || 7200) * 1000;
      const key = el.dataset.storageKey || 'brightroot-sale-ends-at';
      let end = Number(localStorage.getItem(key));
      if (!end || end <= Date.now()) {
        end = Date.now() + duration;
        localStorage.setItem(key, String(end));
      }
      const render = () => {
        let remaining = end - Date.now();
        if (remaining <= 0) {
          end = Date.now() + duration;
          localStorage.setItem(key, String(end));
          remaining = duration;
        }
        qs('[data-hours]', el).textContent = String(Math.floor(remaining / 3600000)).padStart(2, '0');
        qs('[data-minutes]', el).textContent = String(Math.floor((remaining % 3600000) / 60000)).padStart(2, '0');
        qs('[data-seconds]', el).textContent = String(Math.floor((remaining % 60000) / 1000)).padStart(2, '0');
      };
      render();
      setInterval(render, 1000);
    });
  }

  function initMobileMenu() {
    qsa('[data-mobile-menu-toggle]').forEach((button) => {
      const target = document.getElementById(button.getAttribute('aria-controls'));
      if (!target) return;
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        target.hidden = expanded;
      });
    });
  }

  async function getCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  }

  async function changeCart(line, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    return response.json();
  }

  // The cart API doesn't expose compare-at prices, and for subscription lines
  // its "original" price is already the discounted plan price — so real
  // savings need the variant's compare-at / one-time price from product.js.
  const variantMeta = {};
  async function hydrateVariantMeta(cart) {
    const handles = [...new Set(cart.items.filter((item) => item.handle && !variantMeta[item.variant_id]).map((item) => item.handle))];
    await Promise.all(handles.map(async (handle) => {
      try {
        const product = await (await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })).json();
        product.variants.forEach((variant) => {
          variantMeta[variant.id] = { compare: variant.compare_at_price || 0, price: variant.price || 0 };
        });
      } catch (error) { /* product may be unavailable — savings fall back to cart data */ }
    }));
  }

  // Savings per line: anchor is the highest of compare-at, one-time price and
  // the cart's own original price. For Buy-X-Get-Y-Free bundles the anchor is
  // split per bottle across the paid and free rows so the two add up exactly.
  function savingsFor(item) {
    const meta = variantMeta[item.variant_id] || {};
    const anchorLine = Math.max(meta.compare || 0, meta.price || 0, item.original_price || 0) * item.quantity;
    const bundle = (item.variant_title || '').match(/buy\s*(\d+)[,\s]*get\s*(\d+)\s*free/i);
    if (!bundle) {
      return { lineSaving: Math.max(0, anchorLine - item.final_line_price), bonusSaving: 0 };
    }
    const paid = Number(bundle[1]) * item.quantity;
    const free = Number(bundle[2]) * item.quantity;
    const perBottle = anchorLine / (paid + free);
    return {
      lineSaving: Math.max(0, Math.round(perBottle * paid) - item.final_line_price),
      bonusSaving: Math.round(perBottle * free)
    };
  }

  async function renderCart(cart) {
    await hydrateVariantMeta(cart);
    qsa('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
    qsa('[data-cart-description]').forEach((el) => { el.textContent = cart.item_count === 0 ? el.dataset.emptyText : `${cart.item_count} item${cart.item_count === 1 ? '' : 's'} in your bag`; });
    qsa('[data-cart-empty-state]').forEach((el) => { el.hidden = cart.item_count > 0; });
    qsa('[data-cart-filled]').forEach((el) => { el.hidden = cart.item_count === 0; });
    qsa('[data-cart-subtotal]').forEach((el) => { el.textContent = money(cart.items_subtotal_price); });
    const totalSavings = cart.items.reduce((sum, item) => {
      const saving = savingsFor(item);
      return sum + saving.lineSaving + saving.bonusSaving;
    }, 0);
    qsa('[data-cart-savings]').forEach((el) => {
      el.hidden = totalSavings <= 0;
      const amount = qs('[data-cart-savings-amount]', el);
      if (amount) amount.textContent = `−${money(totalSavings)}`;
    });
    const threshold = Number(document.body.dataset.freeShippingThreshold || 4000);
    const remaining = Math.max(0, threshold - cart.items_subtotal_price);
    const progress = Math.min(100, (cart.items_subtotal_price / threshold) * 100);
    qsa('[data-free-shipping-message]').forEach((el) => { el.textContent = remaining <= 0 ? el.dataset.unlocked : el.dataset.remaining.replace('[amount]', money(remaining)); });
    qsa('[data-free-shipping-progress]').forEach((el) => { el.style.setProperty('--progress', `${progress}%`); });
    qsa('[data-cart-items]').forEach((container) => {
      if (!cart.items.length) {
        container.innerHTML = '';
        return;
      }
      container.innerHTML = cart.items.map((item, index) => {
        const optionValues = Array.isArray(item.options_with_values)
          ? item.options_with_values.map((option) => option.value).filter((value) => value && value !== 'Default Title')
          : [];
        const variantLabel = optionValues.length ? optionValues.join(' • ') : (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '');
        const isSubscription = Boolean(item.selling_plan_allocation) || /subscribe/i.test(variantLabel);
        // final_price reflects app/script discounts and can legitimately be 0
        // (e.g. an auto-added free gift) — never fall back to the full price then.
        const unitPrice = typeof item.final_price === 'number' ? item.final_price : item.price;
        const priceLabel = item.final_line_price === 0
          ? `<s>${money(item.original_line_price || item.price * item.quantity)}</s> FREE`
          : money(unitPrice);
        const image = item.image ? `<img src="${item.image}" alt="${escapeHTML(item.product_title)}" loading="lazy">` : '';
        // "Buy X Get Y Free" bundle variants render as two visual rows: the X
        // paid bottles at the line price, and the Y bonus bottles marked FREE.
        // It's one cart line underneath — the controls stay on the paid row.
        const bundle = variantLabel.match(/buy\s*(\d+)[,\s]*get\s*(\d+)\s*free/i);
        const paidCount = bundle ? Number(bundle[1]) * item.quantity : 0;
        const freeCount = bundle ? Number(bundle[2]) * item.quantity : 0;
        const saving = savingsFor(item);
        const lineSaving = saving.lineSaving;
        const bonusValue = saving.bonusSaving;
        const mainRow = `
        <div class="cart-item">
          <div class="cart-item__image">${image}</div>
          <div class="cart-item__details">
            <h4>${escapeHTML(item.product_title)}</h4>
            ${bundle ? `<p class="cart-item__options">${paidCount} Bottle${paidCount === 1 ? '' : 's'}</p>` : (variantLabel ? `<p class="cart-item__options">${escapeHTML(variantLabel)}</p>` : '')}
            ${isSubscription ? '<p class="cart-item__subscription">Subscribe &amp; Save — skip or cancel anytime</p>' : ''}
            <p class="cart-item__price">${priceLabel}${lineSaving > 0 ? ` <span class="cart-item__saving">(You save ${money(lineSaving)})</span>` : ''}</p>
          </div>
          <div class="cart-item__actions">
            <button class="cart-item__remove" type="button" data-cart-change="${index + 1}" data-quantity="0" aria-label="Remove ${escapeHTML(item.product_title)}">${icon('trash')}</button>
            <div class="qty" aria-label="Quantity">
              <button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity - 1}" aria-label="Decrease quantity">${icon('minus', 12)}</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">${icon('plus', 12)}</button>
            </div>
          </div>
        </div>`;
        const bonusRow = bundle ? `
        <div class="cart-item cart-item--bonus">
          <div class="cart-item__image">${image}</div>
          <div class="cart-item__details">
            <h4>${escapeHTML(item.product_title)}</h4>
            <p class="cart-item__options">${freeCount} Bottle${freeCount === 1 ? '' : 's'} — you get ${freeCount === 1 ? 'it' : 'them'} free</p>
            <p class="cart-item__price">FREE <span class="cart-item__saving">(You save ${money(bonusValue)})</span></p>
          </div>
        </div>` : '';
        return mainRow + bonusRow;
      }).join('');
    });
  }

  function openCart() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    getCart().then(renderCart);
  }

  function closeCart() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
  }

  function initCart() {
    qsa('[data-open-cart]').forEach((button) => button.addEventListener('click', openCart));
    qsa('[data-close-cart]').forEach((button) => button.addEventListener('click', closeCart));
    document.addEventListener('click', (event) => {
      const change = event.target.closest('[data-cart-change]');
      if (change) changeCart(Number(change.dataset.cartChange), Number(change.dataset.quantity)).then(renderCart);
    });
    getCart().then(renderCart).catch(() => {});
    qsa('form[action*="/cart/add"]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        if (!form.hasAttribute('data-ajax-cart')) return;
        event.preventDefault();
        const button = qs('[type="submit"]', form);
        button?.setAttribute('disabled', 'disabled');
        try {
          const response = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('add failed');
          window.location.assign('/checkout');
        } catch (error) {
          button?.removeAttribute('disabled');
          openCart();
        }
      });
    });
  }

  function initProductMedia(root = document) {
    qsa('[data-product-gallery]', root).forEach((gallery) => {
      // Swipeable scroll-snap track: thumbs scroll to a slide, swiping the
      // track (touch or trackpad) keeps the active thumb in sync.
      const track = qs('[data-gallery-track]', gallery);
      if (!track) return;
      const thumbs = qsa('[data-thumb]', gallery);
      const setActive = (index) => {
        thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
      };
      thumbs.forEach((button, index) => {
        button.addEventListener('click', () => {
          track.scrollTo({ left: index * track.clientWidth });
          setActive(index);
        });
      });
      const step = (direction) => {
        const lastIndex = qsa('.buybox-media__slide', track).length - 1;
        const index = Math.max(0, Math.min(lastIndex, Math.round(track.scrollLeft / track.clientWidth) + direction));
        track.scrollTo({ left: index * track.clientWidth });
        setActive(index);
      };
      qs('[data-gallery-prev]', gallery)?.addEventListener('click', () => step(-1));
      qs('[data-gallery-next]', gallery)?.addEventListener('click', () => step(1));
      let scrollDebounce;
      track.addEventListener('scroll', () => {
        clearTimeout(scrollDebounce);
        scrollDebounce = setTimeout(() => {
          setActive(Math.round(track.scrollLeft / track.clientWidth));
        }, 80);
      }, { passive: true });
    });
  }

  function initShipDates(root = document) {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
    const day = date.getDate();
    const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    const label = `${date.toLocaleDateString('en-US', { month: 'long' })} ${day}${suffix}`;
    qsa('[data-ship-date]', root).forEach((el) => { el.textContent = label; });
  }

  /* ---------- Motion utilities ---------- */

  function initReveals(root = document) {
    const targets = qsa('[data-reveal]', root);
    if (!targets.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -5% 0px' });
    targets.forEach((el, index) => {
      const parent = el.closest('[data-reveal-stagger]');
      if (parent) {
        const siblings = qsa('[data-reveal]', parent);
        el.style.setProperty('--reveal-delay', `${Math.min(siblings.indexOf(el), 8) * 70}ms`);
      }
      observer.observe(el);
    });
  }

  function animateNumber(el) {
    const target = parseFloat(el.dataset.countUp || '0');
    const decimals = Number(el.dataset.decimals || 0);
    const suffix = el.dataset.suffix || '';
    const prefix = el.dataset.prefix || '';
    if (reducedMotion) {
      el.textContent = `${prefix}${target.toFixed(decimals)}${suffix}`;
      return;
    }
    const duration = 1300;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = `${prefix}${(target * eased).toFixed(decimals)}${suffix}`;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  function initCountUps(root = document) {
    const targets = qsa('[data-count-up]', root);
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach(animateNumber);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        animateNumber(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.5 });
    targets.forEach((el) => observer.observe(el));
  }

  function initClaimedBars(root = document) {
    qsa('[data-claimed-bar]', root).forEach((bar) => {
      const fill = qs('[data-claimed-fill]', bar);
      if (!fill) return;
      const percent = Math.min(100, Number(bar.dataset.claimedBar || 90));
      const apply = () => fill.style.setProperty('--claimed', `${percent}%`);
      if (reducedMotion || !('IntersectionObserver' in window)) {
        apply();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          requestAnimationFrame(apply);
          observer.unobserve(bar);
        });
      }, { threshold: 0.4 });
      observer.observe(bar);
    });
  }

  function initTimelines(root = document) {
    qsa('[data-timeline]', root).forEach((timeline) => {
      const line = qs('[data-timeline-line]', timeline);
      const stages = qsa('[data-timeline-stage]', timeline);
      if (reducedMotion) {
        line?.style.setProperty('--timeline-progress', '100%');
        stages.forEach((stage) => stage.classList.add('is-active'));
        return;
      }
      const update = () => {
        const rect = timeline.getBoundingClientRect();
        const viewportAnchor = window.innerHeight * 0.7;
        const progress = Math.max(0, Math.min(1, (viewportAnchor - rect.top) / (rect.height || 1)));
        line?.style.setProperty('--timeline-progress', `${progress * 100}%`);
        stages.forEach((stage) => {
          const stageRect = stage.getBoundingClientRect();
          stage.classList.toggle('is-active', stageRect.top < viewportAnchor);
        });
      };
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
    });
  }

  function initVideoCarousels(root = document) {
    qsa('[data-video-carousel]', root).forEach((carousel) => {
      const track = qs('[data-carousel-track]', carousel);
      if (!track) return;
      qsa('[data-carousel-prev]', carousel).forEach((button) => button.addEventListener('click', () => {
        track.scrollBy({ left: -track.clientWidth * 0.7, behavior: reducedMotion ? 'auto' : 'smooth' });
      }));
      qsa('[data-carousel-next]', carousel).forEach((button) => button.addEventListener('click', () => {
        track.scrollBy({ left: track.clientWidth * 0.7, behavior: reducedMotion ? 'auto' : 'smooth' });
      }));
      const videos = qsa('video', carousel);
      if (!videos.length || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && !reducedMotion) video.play().catch(() => {});
          else video.pause();
        });
      }, { threshold: 0.4 });
      videos.forEach((video) => observer.observe(video));
    });
  }

  function initJourneyTabs(root = document) {
    qsa('[data-journey]', root).forEach((journey) => {
      const tabs = qsa('[data-journey-tab]', journey);
      const panels = qsa('[data-journey-panel]', journey);
      if (!tabs.length || tabs.length !== panels.length) return;
      const interval = Math.max(3000, Number(journey.dataset.interval || 6000));
      journey.style.setProperty('--journey-interval', `${interval}ms`);
      let index = 0;
      let timer;
      const activate = (next) => {
        index = (next + tabs.length) % tabs.length;
        tabs.forEach((tab, i) => {
          const isActive = i === index;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', String(isActive));
          if (isActive) {
            const bar = qs('.journey-tab__bar span', tab);
            if (bar) {
              bar.style.animation = 'none';
              void bar.offsetWidth;
              bar.style.animation = '';
            }
          }
        });
        panels.forEach((panel, i) => {
          panel.classList.toggle('is-active', i === index);
          panel.hidden = i !== index;
        });
      };
      const schedule = () => {
        clearInterval(timer);
        if (!reducedMotion) timer = setInterval(() => activate(index + 1), interval);
      };
      tabs.forEach((tab, i) => tab.addEventListener('click', () => {
        activate(i);
        schedule();
      }));
      journey.addEventListener('mouseenter', () => {
        journey.classList.add('is-paused');
        clearInterval(timer);
      });
      journey.addEventListener('mouseleave', () => {
        journey.classList.remove('is-paused');
        schedule();
      });
      activate(0);
      schedule();
    });
  }

  /* ---------- Buy box ---------- */

  function initBuybox(root = document) {
    qsa('[data-buybox]', root).forEach((buybox) => {
      const form = qs('[data-buybox-form]', buybox);
      if (!form) return;
      const variants = JSON.parse(qs('[data-buybox-variants]', buybox)?.textContent || '[]');
      if (!variants.length) return;
      const idInput = qs('input[name="id"]', form);
      const quantityInput = qs('[data-buybox-quantity]', form);
      const sellingPlanInput = qs('input[name="selling_plan"]', form);
      const subscribeCheckbox = qs('[data-subscribe-checkbox]', buybox);
      const tiers = qsa('[data-tier]', buybox);
      const submit = qs('[data-buybox-submit]', form);
      const findVariant = (id) => variants.find((candidate) => String(candidate.id) === String(id));
      const subscribed = () => Boolean(subscribeCheckbox?.checked);

      // All prices derive from the product's own data: the variant price,
      // the store's compare-at price as the strikethrough anchor, and the
      // real selling-plan allocation price when the subscription is checked.
      // Subscription savings stack against the same compare-at anchor.
      // quantity > 1 means the tier is N units of the same variant.
      const pricing = (variant, quantity = 1, applySubscription = false) => {
        const bottles = (variant.bottles || 1) * quantity;
        const oneTime = variant.price * quantity;
        const isSubscribed = applySubscription && subscribed() && variant.selling_plan_id && variant.selling_plan_price;
        const price = isSubscribed ? variant.selling_plan_price * quantity : oneTime;
        const compareBase = (variant.compare_at_price || 0) * quantity;
        let compare = 0;
        if (isSubscribed) compare = Math.max(oneTime, compareBase);
        else if (compareBase > oneTime) compare = compareBase;
        const save = Math.max(0, compare - price);
        const savePercent = compare > 0 ? Math.round((save / compare) * 100) : 0;
        return { price, compare, save, savePercent, bottles, perBottle: Math.round(price / bottles) };
      };

      const crossfade = (el, text) => {
        if (!el || el.textContent === text) return;
        if (reducedMotion) {
          el.textContent = text;
          return;
        }
        el.classList.add('is-swapping');
        setTimeout(() => {
          el.textContent = text;
          el.classList.remove('is-swapping');
        }, 120);
      };

      const tierQuantity = (tier) => Math.max(1, Number(tier?.dataset.quantity || 1));

      const renderTier = (tier) => {
        const variant = findVariant(tier.dataset.variantId);
        if (!variant) return;
        const detail = pricing(variant, tierQuantity(tier), true);
        crossfade(qs('[data-tier-price]', tier), money(detail.price));
        const compareTarget = qs('[data-tier-compare]', tier);
        if (compareTarget) compareTarget.textContent = detail.save > 0 ? money(detail.compare) : '';
        const perBottleTarget = qs('[data-tier-per-bottle]', tier);
        if (perBottleTarget) perBottleTarget.textContent = detail.bottles > 1 ? `${money(detail.perBottle)} per bottle` : '';
        const perNightTarget = qs('[data-tier-per-night]', tier);
        if (perNightTarget) perNightTarget.textContent = `${money(Math.round(detail.price / (detail.bottles * 30)))} per night`;
        const saveTarget = qs('[data-tier-save]', tier);
        if (saveTarget) saveTarget.textContent = detail.save > 0 ? `Save ${money(detail.save)} (${detail.savePercent}%)` : '';
      };

      const renderSelection = () => {
        const selectedTier = tiers.find((tier) => tier.classList.contains('is-selected')) || tiers[0];
        const variant = findVariant(selectedTier?.dataset.variantId) || variants[0];
        const quantity = tierQuantity(selectedTier);
        const detail = pricing(variant, quantity, true);
        idInput.value = variant.id;
        if (quantityInput) quantityInput.value = String(quantity);
        if (sellingPlanInput) {
          const useSubscription = subscribed() && variant.selling_plan_id;
          sellingPlanInput.disabled = !useSubscription;
          sellingPlanInput.value = useSubscription ? variant.selling_plan_id : '';
        }
        qsa('[data-buybox-price]', buybox).forEach((el) => crossfade(el, money(detail.price)));
        qsa('[data-buybox-compare]', buybox).forEach((el) => { el.textContent = detail.save > 0 ? money(detail.compare) : ''; });
        qsa('[data-buybox-save]', buybox).forEach((el) => {
          const prefix = el.dataset.savePrefix ? `${el.dataset.savePrefix}: ` : '';
          el.textContent = detail.save > 0 ? `${prefix}Save ${money(detail.save)}` : '';
          el.hidden = detail.save <= 0;
        });
        qsa('[data-sticky-price]').forEach((el) => crossfade(el, money(detail.price)));
        qsa('[data-sticky-compare]').forEach((el) => { el.textContent = detail.save > 0 ? money(detail.compare) : ''; });
        const bottles = detail.bottles;
        qsa('[data-gift]', buybox).forEach((gift) => {
          const required = Number(gift.dataset.unlockBottles || 1);
          const wasUnlocked = gift.classList.contains('is-unlocked');
          const unlocked = bottles >= required;
          gift.classList.toggle('is-unlocked', unlocked);
          if (unlocked && !wasUnlocked && !reducedMotion) {
            gift.classList.add('just-unlocked');
            setTimeout(() => gift.classList.remove('just-unlocked'), 700);
          }
        });
        if (submit) submit.disabled = !variant.available;
      };

      tiers.forEach((tier) => {
        renderTier(tier);
        tier.addEventListener('click', () => {
          tiers.forEach((candidate) => candidate.classList.toggle('is-selected', candidate === tier));
          const input = qs('input[type="radio"]', tier);
          if (input) input.checked = true;
          renderSelection();
        });
      });
      subscribeCheckbox?.addEventListener('change', () => {
        qs('[data-subscribe-toggle]', buybox)?.classList.toggle('is-selected', subscribeCheckbox.checked);
        tiers.forEach(renderTier);
        renderSelection();
      });
      renderSelection();

      const stickyBar = qs('[data-sticky-atc]');
      if (stickyBar && 'IntersectionObserver' in window) {
        qsa('[data-sticky-submit]', stickyBar).forEach((button) => {
          button.addEventListener('click', () => {
            if (form.requestSubmit) form.requestSubmit();
            else form.submit();
          });
        });
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        }, { threshold: 0 });
        observer.observe(form);
      }
    });
  }

  /* ---------- Urgency: live viewers + recent-activity toasts ---------- */

  function initViewerCounts(root = document) {
    qsa('[data-viewer-count]', root).forEach((el) => {
      const base = Math.max(3, Number(el.textContent) || 15);
      let current = base + Math.floor(Math.random() * 5) - 2;
      const render = () => { el.textContent = String(current); };
      render();
      setInterval(() => {
        const drift = Math.random() < 0.5 ? -1 : 1;
        current = Math.max(Math.round(base * 0.6), Math.min(Math.round(base * 1.5), current + drift));
        render();
      }, 6000 + Math.random() * 5000);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initAnnouncementTimers();
    initMobileMenu();
    initCart();
    initProductMedia();
    initShipDates();
    initReveals();
    initCountUps();
    initClaimedBars();
    initTimelines();
    initVideoCarousels();
    initJourneyTabs();
    initBuybox();
    initViewerCounts();
  });
  document.addEventListener('shopify:section:load', (event) => {
    initProductMedia(event.target);
    initShipDates(event.target);
    initReveals(event.target);
    initCountUps(event.target);
    initClaimedBars(event.target);
    initTimelines(event.target);
    initVideoCarousels(event.target);
    initJourneyTabs(event.target);
    initBuybox(event.target);
  });
})();
